import React, { useRef, useMemo } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

/**
 * Renders clickable vertex markers at each point along the trajectory
 * User can click vertices to trigger predictions at that specific step
 */
const TrajectoryVertices = ({ stock, onVertexClick, selectedStep = null }) => {
  const groupRef = useRef();
  const { camera, raycaster, mouse, gl } = useThree();
  const verticesRef = useRef([]);

  const vertices = useMemo(() => {
    if (!stock || !stock.trajectory) return [];
    return stock.trajectory.map((point, stepIndex) => ({
      position: [point.x, point.y, point.z],
      step: stepIndex,
    }));
  }, [stock]);

  // Handle click detection via raycasting
  React.useEffect(() => {
    const handlePointerDown = (event) => {
      if (!groupRef.current) return;

      // Calculate mouse position in normalized device coordinates
      const rect = gl.domElement.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update raycaster
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera);

      // Check intersections with vertices
      const intersects = raycaster.intersectObjects(verticesRef.current, true);

      if (intersects.length > 0) {
        const clickedObject = intersects[0].object;
        const stepIndex = clickedObject.userData?.step;
        if (stepIndex !== undefined && onVertexClick) {
          onVertexClick(stepIndex);
        }
      }
    };

    gl.domElement.addEventListener('pointerdown', handlePointerDown);
    return () => gl.domElement.removeEventListener('pointerdown', handlePointerDown);
  }, [gl, camera, raycaster, onVertexClick]);

  return (
    <group ref={groupRef}>
      {vertices.map((vertex, idx) => {
        const isSelected = selectedStep === vertex.step;
        const size = isSelected ? 0.15 : 0.06;
        const color = isSelected ? '#ffff00' : '#00ffff';

        return (
          <mesh
            key={`vertex-${idx}`}
            position={vertex.position}
            ref={(mesh) => {
              if (mesh) {
                mesh.userData.step = vertex.step;
                if (!verticesRef.current.includes(mesh)) {
                  verticesRef.current[idx] = mesh;
                }
              }
            }}
          >
            <sphereGeometry args={[size, 6, 6]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={isSelected ? 0.8 : 0.4}
              toneMapped={false}
            />
          </mesh>
        );
      })}
    </group>
  );
};

export default TrajectoryVertices;
